import { useEffect, useMemo, useRef, useState } from "react";

import { readableCurveError } from "./errors";
import { GraphRegionSelector } from "./GraphRegionSelector";
import { AIPageSelection } from "./AIPageSelection";

import type { CurveImportApi } from "./api";
import type {
  AICurveCropAudit,
  AICurveExtractionRunRead,
  AISuggestedChart,
  ChartRegion,
  AISuggestedCurve,
  CurveDraftDocument,
  CurveImportCapabilities,
  CurveImportRead,
  CurveKind,
} from "./types";

interface CurveAnalysisPanelProps {
  onBeforeAnalysis?: () => Promise<boolean>;
  api: CurveImportApi;
  capabilities: CurveImportCapabilities | null;
  document: CurveDraftDocument;
  graphEditorRequest?: number;
  hasUnsavedChanges: boolean;
  graphSelectionDisabled?: boolean;
  imported: CurveImportRead;
  onBusyChange?: (busy: boolean) => void;
  onError: (message: string) => void;
  onGraphSelectionDirty?: (dirty: boolean) => void;
  onImported: (imported: CurveImportRead) => void;
  onNotice: (message: string) => void;
}

const AI_REQUEST_STATES = new Set<CurveImportRead["state"]>([
  "parsed",
  "needs_calibration",
  "needs_review",
]);

const KIND_LABELS: Record<CurveKind, string> = {
  brake_power: "Power",
  brake_power_contour: "Power contour",
  efficiency: "Efficiency",
  efficiency_contour: "Efficiency contour",
  head: "Flow and head",
  npshr: "NPSHr",
  npshr_contour: "NPSHr contour",
  operating_boundary: "Operating limit",
  suction_lift: "Suction lift",
};

function confidenceLabel(value: number): string {
  return Math.round(value * 100) + "%";
}

function valueOrDash(value: string | number | null): string {
  return value === null || value === "" ? "Not found" : String(value);
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter((value) => value.trim() !== ""))];
}

function unavailableLabel(reason: string | null): string {
  if (reason === "ai_disabled") return "Analysis is disabled.";
  if (reason === "ai_api_key_missing" || reason === "ai_api_key_invalid") {
    return "Analysis is not configured.";
  }
  return "Analysis is unavailable.";
}

function curveCoverage(curves: AISuggestedCurve[], kinds?: CurveKind[]) {
  const selected =
    kinds === undefined
      ? curves
      : curves.filter((curve) => kinds.includes(curve.kind));
  return {
    withPoints: selected.filter((curve) => curve.source_points.length >= 2).length,
    labelOnly: selected.filter((curve) => curve.source_points.length === 0).length,
    incomplete: selected.filter((curve) => curve.source_points.length === 1).length,
  };
}

function curveCountLabel(count: number): string {
  return `${count} curve${count === 1 ? "" : "s"}`;
}

function CurveCoverageCount({
  counts,
}: {
  counts: ReturnType<typeof curveCoverage>;
}) {
  return (
    <dd>
      <span>{curveCountLabel(counts.withPoints)} with points</span>
      {counts.labelOnly === 0 ? null : (
        <small> · {counts.labelOnly} label only / no points</small>
      )}
      {counts.incomplete === 0 ? null : (
        <small> · {counts.incomplete} incomplete / one point</small>
      )}
    </dd>
  );
}

function timeoutLabel(seconds: number): string {
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

function curveSpeeds(run: AICurveExtractionRunRead): number[] {
  return [
    ...new Set(
      [
        ...run.suggestion.metadata.speeds_rpm,
        ...run.suggestion.charts.flatMap((chart) =>
          chart.curves.flatMap((curve) =>
            curve.speed_rpm === null ? [] : [curve.speed_rpm],
          ),
        ),
      ].filter((speed) => Number.isInteger(speed) && speed > 0),
    ),
  ].sort((left, right) => left - right);
}

function chartFlags(run: AICurveExtractionRunRead): string[] {
  return unique([
    ...run.suggestion.warnings,
    ...(run.suggestion.insufficiency_reason === null
      ? []
      : [run.suggestion.insufficiency_reason]),
    ...run.suggestion.charts.flatMap((chart) => [
      ...chart.review_notes,
      ...(chart.x_axis?.review_notes ?? []),
      ...chart.y_axes.flatMap((axis) => axis.review_notes ?? []),
      ...chart.curves.flatMap((curve) => [
        ...curve.review_notes,
        ...curve.source_points.flatMap((point) => point.review_notes ?? []),
      ]),
    ]),
  ]);
}

function overlayCurves(chart: AISuggestedChart) {
  return chart.curves.flatMap((curve, index) => {
    if (
      curve.source_points.length < 2 ||
      curve.source_points.some(
        (point) =>
          point.page_x_normalized === null || point.page_y_normalized === null,
      )
    ) {
      return [];
    }
    const points = curve.source_points
      .map(
        (point) =>
          String((point.page_x_normalized ?? 0) * 1000) +
          "," +
          String((point.page_y_normalized ?? 0) * 1000),
      )
      .join(" ");
    return [{ curve, index, points }];
  });
}

const CROP_REGION_TOLERANCE = 1e-6;

function cropMatchesSavedChart(
  run: AICurveExtractionRunRead,
  selectedChart: ChartRegion | null,
): boolean {
  const crop = run.audit.chart_crop ?? null;
  if (crop === null) return true;
  if (
    selectedChart === null ||
    selectedChart.page_number !== crop.page_number ||
    crop.page_width_px <= 0 ||
    crop.page_height_px <= 0
  ) {
    return false;
  }
  const current = {
    bottom: Math.ceil(selectedChart.bottom_px) / crop.page_height_px,
    left: Math.floor(selectedChart.left_px) / crop.page_width_px,
    right: Math.ceil(selectedChart.right_px) / crop.page_width_px,
    top: Math.floor(selectedChart.top_px) / crop.page_height_px,
  };
  return (
    Math.abs(current.left - crop.region.left) <= CROP_REGION_TOLERANCE &&
    Math.abs(current.top - crop.region.top) <= CROP_REGION_TOLERANCE &&
    Math.abs(current.right - crop.region.right) <= CROP_REGION_TOLERANCE &&
    Math.abs(current.bottom - crop.region.bottom) <= CROP_REGION_TOLERANCE
  );
}

function SourceOverlayPage({
  api,
  chart,
  cropAudit = null,
  enlarged = false,
  importId,
}: {
  api: CurveImportApi;
  chart: AISuggestedChart;
  cropAudit?: AICurveCropAudit | null;
  enlarged?: boolean;
  importId: string;
}) {
  const overlays = overlayCurves(chart);
  const anchors = [chart.x_axis, ...chart.y_axes].flatMap((axis) =>
    (axis?.anchors ?? []).map((anchor) => ({ ...anchor, quantity: axis?.quantity, unit: axis?.unit })),
  );
  const crop =
    enlarged &&
    cropAudit !== null &&
    cropAudit.page_number === chart.page_number
      ? cropAudit
      : null;
  const cropWidth =
    crop === null ? 1 : crop.region.right - crop.region.left;
  const cropHeight =
    crop === null ? 1 : crop.region.bottom - crop.region.top;
  const cropAspectRatio =
    crop === null
      ? null
      : (crop.page_width_px * cropWidth) /
        (crop.page_height_px * cropHeight);
  const page = (
    <div
      className={
        "curve-analysis-source__page" + (enlarged ? " is-enlarged" : "")
      }
      style={
        crop === null
          ? undefined
          : {
              transform: `translate(${-crop.region.left * 100}%, ${-crop.region.top * 100}%)`,
              width: `${100 / cropWidth}%`,
            }
      }
    >
      <img
        alt={
          (enlarged ? "Enlarged vendor sheet page " : "Vendor sheet page ") +
          chart.page_number
        }
        src={api.pageUrl(importId, chart.page_number)}
      />
      {chart.region === null && overlays.length === 0 && anchors.length === 0 ? null : (
        <svg
          aria-label={
            enlarged
              ? "Enlarged extracted curve overlay"
              : "Extracted curve overlay"
          }
          className="curve-analysis-overlay"
          preserveAspectRatio="none"
          role="img"
          viewBox="0 0 1000 1000"
        >
          <title>Extracted chart region and curves</title>
          {chart.region === null ? null : (
            <rect
              className="curve-analysis-overlay__region"
              height={(chart.region.bottom - chart.region.top) * 1000}
              width={(chart.region.right - chart.region.left) * 1000}
              x={chart.region.left * 1000}
              y={chart.region.top * 1000}
            />
          )}
          {overlays.map(({ curve, index, points }) => (
            <polyline
              key={curve.kind + ":" + curve.label + ":" + index}
              className={"curve-analysis-overlay__curve is-" + curve.kind}
              fill="none"
              points={points}
              vectorEffect="non-scaling-stroke"
            >
              <title>
                {curve.label + ", " + confidenceLabel(curve.confidence)}
              </title>
            </polyline>
          ))}
          {anchors.map((anchor, index) => (
            <circle
              key={index}
              data-calibration-anchor="true"
              cx={anchor.page_x_normalized * 1000}
              cy={anchor.page_y_normalized * 1000}
              r={5}
              fill="#fff"
              stroke="#0b5cab"
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
            >
              <title>{anchor.quantity ?? "Axis"}: {anchor.value} {anchor.unit ?? ""}</title>
            </circle>
          ))}
        </svg>
      )}
    </div>
  );
  if (crop === null || cropAspectRatio === null) return page;
  return (
    <div
      aria-label="Selected graph crop"
      className="curve-analysis-source__crop"
      role="group"
      style={{ aspectRatio: String(cropAspectRatio) }}
    >
      {page}
    </div>
  );
}

function SourceOverlay({
  api,
  chart,
  cropAudit,
  importId,
}: {
  api: CurveImportApi;
  chart: AISuggestedChart;
  cropAudit: AICurveCropAudit | null;
  importId: string;
}) {
  const [enlarged, setEnlarged] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const enlargeButtonRef = useRef<HTMLButtonElement | null>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!enlarged) return;
    const returnFocus = enlargeButtonRef.current;
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setEnlarged(false);
        return;
      }
      if (event.key !== "Tab") return;
      const controls = surfaceRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (controls === undefined || controls.length === 0) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (
        first === last ||
        (event.shiftKey && document.activeElement === first) ||
        (!event.shiftKey && document.activeElement === last)
      ) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      returnFocus?.focus();
    };
  }, [enlarged]);

  return (
    <figure className="curve-analysis-source">
      <SourceOverlayPage
        api={api}
        chart={chart}
        cropAudit={cropAudit}
        importId={importId}
      />
      <figcaption>
        <span>
          Page {chart.page_number}
          {chart.label === null ? "" : " · " + chart.label}
        </span>
        <button
          ref={enlargeButtonRef}
          aria-haspopup="dialog"
          type="button"
          onClick={() => setEnlarged(true)}
        >
          Enlarge graph
        </button>
      </figcaption>
      {enlarged ? (
        <div
          aria-labelledby="curve-analysis-lightbox-title"
          aria-modal="true"
          className="curve-analysis-lightbox"
          role="dialog"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setEnlarged(false);
          }}
        >
          <div
            ref={surfaceRef}
            className="curve-analysis-lightbox__surface"
          >
            <header>
              <div>
                <p className="eyebrow">Source overlay</p>
                <h3 id="curve-analysis-lightbox-title">
                  Enlarged source graph
                </h3>
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={() => setEnlarged(false)}
              >
                Close
              </button>
            </header>
            <SourceOverlayPage
              enlarged
              api={api}
              chart={chart}
              cropAudit={cropAudit}
              importId={importId}
            />
          </div>
        </div>
      ) : null}
    </figure>
  );
}

function CurveDetails({ chart }: { chart: AISuggestedChart }) {
  return (
    <details className="curve-analysis-corrections">
      <summary>Suggested curves ({chart.curves.length})</summary>
      <div className="curve-analysis-curve-list">
        {chart.curves.map((curve, index) => (
          <details key={curve.kind + ":" + curve.label + ":" + index}>
            <summary>
              <span>{curve.label}</span>
              <small>
                {curve.speed_rpm === null ? "" : curve.speed_rpm + " RPM · "}
                {curve.source_points.length === 0
                  ? "Label only / no points · "
                  : curve.source_points.length === 1
                    ? "One point / incomplete curve · "
                    : ""}
                {confidenceLabel(curve.confidence)}
              </small>
            </summary>
            <dl>
              <div>
                <dt>Type</dt>
                <dd>{KIND_LABELS[curve.kind]}</dd>
              </div>
              <div>
                <dt>Points</dt>
                <dd>{curve.source_points.length}</dd>
              </div>
              <div>
                <dt>Contour</dt>
                <dd>
                  {curve.contour_value === null
                    ? "—"
                    : curve.contour_value +
                      (curve.contour_unit === null
                        ? ""
                        : " " + curve.contour_unit)}
                </dd>
              </div>
            </dl>
            {curve.review_notes.length === 0 ? null : (
              <ul>
                {curve.review_notes.map((note, noteIndex) => (
                  <li key={noteIndex + ":" + note}>{note}</li>
                ))}
              </ul>
            )}
            {curve.source_points.length === 0 ? (
              <p>
                No points were extracted for this label. Add curve points manually.
              </p>
            ) : (
              <div className="curve-analysis-points">
                <table aria-label={curve.label + " extracted points"}>
                  <thead>
                    <tr>
                      <th scope="col">Flow</th>
                      <th scope="col">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {curve.source_points.map((point, pointIndex) => (
                      <tr key={pointIndex + ":" + point.x + ":" + point.y}>
                        <td>{point.x}</td>
                        <td>{point.y}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </details>
        ))}
      </div>
    </details>
  );
}

export function CurveAnalysisPanel({
  onBeforeAnalysis,
  api,
  capabilities,
  document,
  graphEditorRequest = 0,
  hasUnsavedChanges,
  graphSelectionDisabled,
  imported,
  onBusyChange,
  onError,
  onGraphSelectionDirty,
  onImported,
  onNotice,
}: CurveAnalysisPanelProps) {
  const [requestedRun, setRequestedRun] =
    useState<AICurveExtractionRunRead | null>(null);
  const [supportingPages, setSupportingPages] = useState<number[]>([]);
  const [requesting, setRequesting] = useState(false);
  const [analysisError, setAnalysisError] = useState("");
  const [analysisElapsedSeconds, setAnalysisElapsedSeconds] = useState(0);
  const [materializing, setMaterializing] = useState(false);
  const [materialized, setMaterialized] = useState(false);
  const [activeChartIndex, setActiveChartIndex] = useState(0);
  const [analyzeFocusRequest, setAnalyzeFocusRequest] = useState(0);
  const analyzeActionRef = useRef<HTMLDivElement | null>(null);
  const analysisFeedbackRef = useRef<HTMLDivElement | null>(null);
  const graphEditorRef = useRef<HTMLDetailsElement | null>(null);
  const requestPendingRef = useRef(false);
  const activeImportRef = useRef(imported.id);
  activeImportRef.current = imported.id;
  const mountedRef = useRef(true);

  const selectorDisabled =
    (graphSelectionDisabled ?? hasUnsavedChanges) ||
    requesting ||
    materializing;

  useEffect(() => {
    setRequestedRun(null);
    setSupportingPages([]);
    setAnalysisError("");
    setMaterialized(false);
    setActiveChartIndex(0);
  }, [imported.id]);

  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!requesting) return;
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      setAnalysisElapsedSeconds(Math.floor((Date.now() - startedAt) / 1_000));
    }, 1_000);
    return () => window.clearInterval(interval);
  }, [requesting]);

  useEffect(() => {
    if (graphEditorRequest === 0) return;
    const details = graphEditorRef.current;
    if (details === null) return;
    details.open = true;
    details.querySelector("summary")?.focus();
    if (typeof details.scrollIntoView === "function") {
      details.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [graphEditorRequest]);

  const storedRun = imported.suggestions.ai_suggestion_runs.at(-1) ?? null;
  const run = requestedRun ?? storedRun;
  const withinAiLimits =
    capabilities !== null &&
    imported.page_count !== null &&
    supportingPages.length + 1 <= capabilities.ai_max_pdf_pages;
  const belowAiRunLimit =
    capabilities !== null &&
    imported.suggestions.ai_suggestion_runs.length <
      capabilities.ai_max_runs_per_import;
  const selectedChart = imported.selected_chart;
  const hasSavedChart =
    selectedChart !== null &&
    imported.selected_page === selectedChart.page_number;
  const legacyDraftWithoutGraph =
    !hasSavedChart && storedRun === null && document.traces.length > 0;
  const runMatchesSavedChart =
    run === null || cropMatchesSavedChart(run, selectedChart);

  useEffect(() => {
    setSupportingPages([]);
    setAnalysisError("");
  }, [selectedChart]);

  const requestEligible =
    capabilities?.ai_document_suggestions_available === true &&
    imported.source_format === "pdf" &&
    AI_REQUEST_STATES.has(imported.state) &&
    withinAiLimits &&
    belowAiRunLimit &&
    hasSavedChart;
  const activeChart = run?.suggestion.charts[activeChartIndex] ?? null;
  const curves = useMemo(
    () => run?.suggestion.charts.flatMap((chart) => chart.curves) ?? [],
    [run],
  );
  const flags = run === null ? [] : chartFlags(run);
  const sourceNotes =
    run === null
      ? []
      : unique(
          run.suggestion.charts.flatMap((chart) => chart.source_notes ?? []),
        );
  const sourceLimits =
    run === null
      ? []
      : unique(
          run.suggestion.charts.flatMap((chart) => chart.source_limits ?? []),
        );
  const speeds = run === null ? [] : curveSpeeds(run);
  const alreadyMaterialized =
    materialized ||
    (run !== null &&
      document.traces.some(
        (trace) =>
          trace.source_ai_request_id === run.audit.local_request_id ||
          trace.source_ai_response_sha256 === run.audit.response_sha256 ||
          trace.suggested_by === "openai:" + run.audit.local_request_id,
      ));

  const counts = useMemo(
    () => ({
      efficiency: curveCoverage(curves, ["efficiency", "efficiency_contour"]),
      head: curveCoverage(curves, ["head"]),
      npshr: curveCoverage(curves, ["npshr", "npshr_contour"]),
      power: curveCoverage(curves, ["brake_power", "brake_power_contour"]),
      total: curveCoverage(curves),
    }),
    [curves],
  );

  const acceptGraphImport = (next: CurveImportRead) => {
    onImported(next);
    if (next.selected_chart !== null) {
      setAnalyzeFocusRequest((request) => request + 1);
    }
  };

  useEffect(() => {
    if (analyzeFocusRequest === 0) return;
    const action = analyzeActionRef.current;
    if (action === null) return;
    const disclosure = action.closest<HTMLDetailsElement>(
      "details.curve-analysis-again",
    );
    if (disclosure !== null) disclosure.open = true;
    action.focus({ preventScroll: true });
    if (typeof action.scrollIntoView === "function") {
      action.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [analyzeFocusRequest, hasSavedChart, requestEligible]);

  const requestSuggestions = async () => {
    if (
      requestPendingRef.current ||
      !requestEligible ||
      selectedChart === null ||
      hasUnsavedChanges
    )
      return;
    requestPendingRef.current = true;
    const requestedImportId = imported.id;
    setAnalysisError("");
    onError("");
    setAnalysisElapsedSeconds(0);
    setRequesting(true);
    onBusyChange?.(true);
    try {
      if (onBeforeAnalysis !== undefined && !await onBeforeAnalysis()) return;
      if (!mountedRef.current || activeImportRef.current !== requestedImportId) return;
      const nextRun = await api.requestAiSuggestions(imported.id, [
        selectedChart.page_number,
        ...supportingPages,
      ]);
      if (!mountedRef.current || activeImportRef.current !== requestedImportId) return;
      setRequestedRun(nextRun);
      setMaterialized(false);
      setActiveChartIndex(0);
      onNotice("Analysis ready for review.");
    } catch (error: unknown) {
      if (!mountedRef.current || activeImportRef.current !== requestedImportId) return;
      const message = readableCurveError(
        error,
        "Analysis failed. Nothing was changed.",
      );
      setAnalysisError(message);
      window.requestAnimationFrame(() => {
        const feedback = analysisFeedbackRef.current;
        if (feedback === null) return;
        feedback.focus({ preventScroll: true });
        if (typeof feedback.scrollIntoView === "function") {
          feedback.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      });
    } finally {
      requestPendingRef.current = false;
      if (mountedRef.current) {
        setRequesting(false);
        onBusyChange?.(false);
      }
    }
  };

  const materializeSuggestions = async () => {
    if (
      run === null ||
      materializing ||
      alreadyMaterialized ||
      hasUnsavedChanges ||
      !runMatchesSavedChart ||
      document.traces.length > 0 ||
      run.suggestion.insufficient_for_curve_extraction ||
      run.suggestion.charts.length === 0
    ) {
      return;
    }
    setMaterializing(true);
    onBusyChange?.(true);
    try {
      const next = await api.materializeAiSuggestions(
        imported.id,
        run.audit.local_request_id,
        run.audit.response_sha256,
      );
      setMaterialized(true);
      onImported(next);
      window.requestAnimationFrame(() => {
        const target =
          window.document.getElementById("curve-findings-title") ??
          window.document.getElementById("curve-save-title");
        if (!(target instanceof HTMLElement)) return;
        target.focus({ preventScroll: true });
        if (typeof target.scrollIntoView === "function") {
          target.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      });
      onNotice("Extracted curves added for review.");
    } catch (error: unknown) {
      onError(
        readableCurveError(
          error,
          "Extracted curves could not be added. The draft was not changed.",
        ),
      );
    } finally {
      setMaterializing(false);
      onBusyChange?.(false);
    }
  };

  const analysisWait = timeoutLabel(capabilities?.ai_timeout_seconds ?? 180);

  const analysisControls = (
    <div
      ref={analyzeActionRef}
      aria-busy={requesting}
      aria-label="Analyze curve"
      className="curve-analysis-consent"
      role="group"
      tabIndex={-1}
    >
      {selectedChart === null ? null : (
        <AIPageSelection
          chartPage={selectedChart.page_number}
          pageCount={imported.page_count ?? selectedChart.page_number}
          supportingPages={supportingPages}
          maxPages={capabilities?.ai_max_pdf_pages ?? 1}
          disabled={requesting}
          onChange={setSupportingPages}
        />
      )}
      <button
        className="curve-primary-action"
        disabled={requesting || hasUnsavedChanges}
        type="button"
        onClick={() => void requestSuggestions()}
      >
        {requesting ? "Analyzing…" : "Analyze curve"}
      </button>
      <small>AI-assisted analysis · <a href="/privacy#pump-curve-analysis" target="_blank" rel="noreferrer">Privacy policy</a></small>
      {requesting ? (
        <div
          aria-live="polite"
          className="curve-analysis-request-feedback"
          role="status"
        >
          <strong aria-hidden="true">
            Analyzing… {analysisElapsedSeconds}s
          </strong>
          <span className="visually-hidden">Analysis in progress.</span>
          <span>May take up to {analysisWait}.</span>
          <progress aria-label="OpenAI analysis in progress" />
        </div>
      ) : analysisError === "" ? null : (
        <div
          ref={analysisFeedbackRef}
          className="curve-analysis-request-feedback is-error"
          role="alert"
          tabIndex={-1}
        >
          {analysisError}
        </div>
      )}
    </div>
  );

  if (run === null) {
    return (
      <section
        aria-labelledby="curve-analysis-title"
        className="curve-analysis-panel"
      >
        <div className="curve-simple-heading">
          <div>
            <p className="eyebrow">
              {hasSavedChart
                ? "Step 2"
                : legacyDraftWithoutGraph
                  ? "Optional"
                  : "Step 1"}
            </p>
            <h2 id="curve-analysis-title">
              {hasSavedChart || legacyDraftWithoutGraph
                ? "Analyze curve"
                : "Select graph"}
            </h2>
          </div>
        </div>
        {!hasSavedChart && !legacyDraftWithoutGraph ? (
          <GraphRegionSelector
            api={api}
            disabled={selectorDisabled}
            document={document}
            importId={imported.id}
            onBusyChange={onBusyChange}
            onDirtyChange={onGraphSelectionDirty}
            onError={onError}
            onImported={acceptGraphImport}
            onNotice={onNotice}
            pageCount={imported.page_count}
          />
        ) : null}
        {legacyDraftWithoutGraph ? (
          <details className="curve-graph-change" ref={graphEditorRef}>
            <summary>Select graph to analyze</summary>
            <GraphRegionSelector
              api={api}
              disabled={selectorDisabled}
              document={document}
              importId={imported.id}
              onBusyChange={onBusyChange}
              onDirtyChange={onGraphSelectionDirty}
              onError={onError}
              onImported={acceptGraphImport}
              onNotice={onNotice}
              pageCount={imported.page_count}
            />
          </details>
        ) : null}
        {hasSavedChart && capabilities === null ? (
          <p>Checking analysis…</p>
        ) : null}
        {capabilities?.ai_document_suggestions_available === false ? (
          <p className="curve-analysis-unavailable">
            {unavailableLabel(capabilities.ai_unavailable_reason)} Enter values
            manually.
          </p>
        ) : null}
        {capabilities?.ai_document_suggestions_available === true &&
        !withinAiLimits ? (
          <p className="curve-analysis-unavailable">
            The selected pages exceed the analysis limit, or the page count is not yet known. Enter values manually or reduce the selection.
          </p>
        ) : null}
        {capabilities?.ai_document_suggestions_available === true &&
        withinAiLimits &&
        !belowAiRunLimit ? (
          <p className="curve-analysis-unavailable">
            This sheet has reached its analysis limit.
          </p>
        ) : null}
        {requestEligible ? (
          <>
            <details className="curve-graph-change" ref={graphEditorRef}>
              <summary>
                {hasSavedChart ? "Change graph" : "Select graph"}
              </summary>
              <GraphRegionSelector
                api={api}
                disabled={selectorDisabled}
                document={document}
                importId={imported.id}
                onBusyChange={onBusyChange}
                onDirtyChange={onGraphSelectionDirty}
                onError={onError}
                onImported={acceptGraphImport}
                onNotice={onNotice}
                pageCount={imported.page_count}
              />
            </details>
            {analysisControls}
          </>
        ) : null}
      </section>
    );
  }

  const metadata = run.suggestion.metadata;
  const canMaterialize =
    runMatchesSavedChart &&
    !hasUnsavedChanges &&
    !alreadyMaterialized &&
    document.traces.length === 0 &&
    !materializing &&
    !run.suggestion.insufficient_for_curve_extraction &&
    run.suggestion.charts.length > 0;

  return (
    <section
      aria-labelledby="curve-analysis-title"
      className="curve-analysis-panel"
    >
      <div className="curve-simple-heading">
        <div>
          <p className="eyebrow">Step 2</p>
          <h2 id="curve-analysis-title">Review curve data</h2>
        </div>
        <span className="curve-analysis-status">Unverified</span>
      </div>

      <dl className="curve-analysis-identity" aria-label="Pump sheet summary">
        <div>
          <dt>Pump</dt>
          <dd>
            {[metadata.manufacturer, metadata.model]
              .filter(Boolean)
              .join(" ") || "Not found"}
          </dd>
        </div>
        <div>
          <dt>Size</dt>
          <dd>{valueOrDash(metadata.pump_size_text)}</dd>
        </div>
        <div>
          <dt>Impeller</dt>
          <dd>{valueOrDash(metadata.impeller_diameter_text)}</dd>
        </div>
        <div>
          <dt>Drawing</dt>
          <dd>
            {[metadata.drawing_number, metadata.curve_revision]
              .filter(Boolean)
              .join(" · ") || "Not found"}
          </dd>
        </div>
      </dl>

      <section aria-labelledby="curve-analysis-speeds-title">
        <h3 id="curve-analysis-speeds-title">Detected RPM</h3>
        <div className="curve-analysis-rpms">
          {speeds.length === 0 ? (
            <span className="is-missing">Not found</span>
          ) : (
            speeds.map((speed) => <span key={speed}>{speed}</span>)
          )}
        </div>
      </section>

      <dl
        aria-label="Extracted performance data"
        className="curve-analysis-coverage"
      >
        <div>
          <dt>Flow and head</dt>
          <CurveCoverageCount counts={counts.head} />
        </div>
        <div>
          <dt>Efficiency</dt>
          <CurveCoverageCount counts={counts.efficiency} />
        </div>
        <div>
          <dt>Power</dt>
          <CurveCoverageCount counts={counts.power} />
        </div>
        <div>
          <dt>NPSHr</dt>
          <CurveCoverageCount counts={counts.npshr} />
        </div>
      </dl>

      {counts.total.labelOnly === 0 && counts.total.incomplete === 0 ? null : (
        <section
          aria-labelledby="curve-analysis-incomplete-title"
          className="curve-analysis-flags"
        >
          <h3 id="curve-analysis-incomplete-title">Incomplete curve extraction</h3>
          {counts.total.labelOnly === 0 ? null : (
            <p>
              {counts.total.labelOnly} curve{" "}
              {counts.total.labelOnly === 1 ? "label has" : "labels have"} no traced
              points. Labels alone cannot provide performance values.
            </p>
          )}
          {counts.total.incomplete === 0 ? null : (
            <p>
              {curveCountLabel(counts.total.incomplete)}{" "}
              {counts.total.incomplete === 1 ? "has" : "have"} only one point,
              which is not enough to define a curve.
            </p>
          )}
          <p>
            {counts.total.withPoints > 0
              ? "Review the curves with points and add missing curve data manually."
              : "Add missing curve data manually or analyze again."}
          </p>
        </section>
      )}

      <div className="curve-analysis-action">
        <button
          className="curve-primary-action"
          disabled={!canMaterialize}
          type="button"
          onClick={() => void materializeSuggestions()}
        >
          {materializing
            ? "Adding curves…"
            : alreadyMaterialized
              ? "Curves added"
              : "Use extracted data"}
        </button>
        <small>
          {!runMatchesSavedChart
            ? "Analyze this graph first."
            : hasUnsavedChanges
              ? "Check current edits first."
              : document.traces.length > 0 && !alreadyMaterialized
                ? "Clear current curves first."
                : alreadyMaterialized
                  ? "Continue below."
                  : "Review before saving."}
        </small>
      </div>

      {sourceNotes.length === 0 && sourceLimits.length === 0 ? null : (
        <section
          aria-labelledby="sheet-notes-title"
          className="curve-analysis-source-notes"
        >
          <h3 id="sheet-notes-title">Sheet notes</h3>
          <dl>
            {sourceNotes.length === 0 ? null : (
              <div>
                <dt>Notes</dt>
                <dd>{sourceNotes.join(" · ")}</dd>
              </div>
            )}
            {sourceLimits.length === 0 ? null : (
              <div>
                <dt>Limits</dt>
                <dd>{sourceLimits.join(" · ")}</dd>
              </div>
            )}
          </dl>
        </section>
      )}

      {flags.length === 0 ? null : (
        <section className="curve-analysis-flags" aria-labelledby="flags-title">
          <h3 id="flags-title">Check these items</h3>
          <ul>
            {flags.map((flag) => (
              <li key={flag}>{flag}</li>
            ))}
          </ul>
        </section>
      )}

      {run.suggestion.charts.length > 1 ? (
        <div
          aria-label="Extracted chart pages"
          className="curve-analysis-tabs"
          role="group"
        >
          {run.suggestion.charts.map((chart, index) => (
            <button
              key={chart.page_number + ":" + index}
              aria-pressed={activeChartIndex === index}
              type="button"
              onClick={() => setActiveChartIndex(index)}
            >
              Page {chart.page_number}
            </button>
          ))}
        </div>
      ) : null}

      {activeChart === null ? null : (
        <div className="curve-analysis-review">
          <SourceOverlay
            api={api}
            chart={activeChart}
            cropAudit={run.audit.chart_crop ?? null}
            importId={imported.id}
          />
          <div className="curve-analysis-review__summary">
            <div>
              <span>Chart confidence</span>
              <strong>{confidenceLabel(activeChart.confidence)}</strong>
            </div>
            <div>
              <span>Flow axis</span>
              <strong>{activeChart.x_axis?.unit ?? "Not found"}</strong>
            </div>
            <div>
              <span>Vertical axes</span>
              <strong>
                {unique(
                  activeChart.y_axes.flatMap((axis) =>
                    axis.unit === null ? [] : [axis.unit],
                  ),
                ).join(", ") || "Not found"}
              </strong>
            </div>
            <section aria-label="Printed tick calibration">
              <h3>Printed tick calibration</h3>
              <p>Blue circles mark the recorded ticks in the source image. New analysis computes engineering values locally from these anchors and traced positions; confirm the printed values, units, and locations.</p>
              {[activeChart.x_axis, ...activeChart.y_axes].filter((axis) => axis !== null).map((axis, index) => (
                <div key={index}>
                  <strong>{axis.label ?? axis.quantity ?? "Axis"} · {axis.unit ?? "unit unknown"}</strong>
                  {(axis.anchors?.length ?? 0) < 2 ? (
                    <p className="curve-alert">No complete printed-tick calibration was recorded for this axis. Set real tick anchors before relying on its values.</p>
                  ) : (
                    <ul>
                      {axis.anchors?.map((anchor, anchorIndex) => (
                        <li key={anchorIndex}>
                          {anchor.value} {axis.unit} — page x {(anchor.page_x_normalized * 100).toFixed(2)}%, y {(anchor.page_y_normalized * 100).toFixed(2)}%
                          {anchor.label === null ? "" : ` (${anchor.label})`}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>
            <CurveDetails chart={activeChart} />
          </div>
        </div>
      )}

      <details className="curve-analysis-technical">
        <summary>Analysis details</summary>
        <dl>
          <div>
            <dt>Model</dt>
            <dd>{run.audit.returned_model}</dd>
          </div>
          <div>
            <dt>Completed</dt>
            <dd>{new Date(run.audit.completed_at).toLocaleString()}</dd>
          </div>
          <div>
            <dt>Processing authorization</dt>
            <dd>{run.audit.authorization_method === "analyze_action"
              ? `Analyze curve action · ${run.audit.privacy_policy_version ?? run.audit.consent_statement_version}`
              : `Historical checkbox consent · ${run.audit.consent_statement_version}`}</dd>
          </div>
          <div>
            <dt>Request</dt>
            <dd>
              <code>{run.audit.local_request_id}</code>
            </dd>
          </div>
          <div>
            <dt>Source hash</dt>
            <dd>
              <code>{run.audit.source_sha256}</code>
            </dd>
          </div>
          <div>
            <dt>Transmitted source</dt>
            <dd>{run.audit.input_transport === "inline_base64_selected_page_pngs"
              ? `Selected page images and embedded text: ${(run.audit.transmitted_pages ?? []).map((page) => page.page_number).join(", ")}`
              : "Historical request: full original PDF"}</dd>
          </div>
          <div>
            <dt>Response hash</dt>
            <dd>
              <code>{run.audit.response_sha256}</code>
            </dd>
          </div>
          <div>
            <dt>Tokens</dt>
            <dd>
              {run.audit.usage.total_tokens?.toLocaleString() ?? "Not reported"}
            </dd>
          </div>
        </dl>
      </details>

      <details className="curve-graph-change" ref={graphEditorRef}>
        <summary>{hasSavedChart ? "Change graph" : "Select graph"}</summary>
        <GraphRegionSelector
          api={api}
          disabled={selectorDisabled}
          document={document}
          importId={imported.id}
          onBusyChange={onBusyChange}
          onDirtyChange={onGraphSelectionDirty}
          onError={onError}
          onImported={acceptGraphImport}
          onNotice={onNotice}
          pageCount={imported.page_count}
        />
      </details>
      {requestEligible ? (
        <details className="curve-analysis-again">
          <summary>Analyze again</summary>
          {analysisControls}
        </details>
      ) : null}
    </section>
  );
}
